import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense.ts";
import { z } from "zod";
import useTranslation from "@babackapp/translations/use-translation";
import type { ReturnRequestDto } from "@babackapp/private-api-client/types";
import { useForm, useWatch } from "react-hook-form";
import { useEffect } from "react";
import { Form, FormField } from "@babackapp/ui/components/form";
import FormSwitch from "@babackapp/react-hook-forms-components/form-switch";
import useUpdateReturnRequestDeactivateAutoTreatLogistician from "@babackapp/private-api-client/client/use-update-return-request-deactivate-auto-treat-logistician";
import useViewerRole from "@/lib/rights/use-viewer-role.ts";

type ReturnRequestDeactivateAutoTreatLogisticianProps = {
  returnRequest: ReturnRequestDto;
};
const ReturnRequestDeactivateAutoTreatLogisticianFormSchema = z.object({
  deactivateAutoTreatLogistician: z.boolean(),
});

export type ReturnRequestDeactivateAutoTreatLogisticianFormValues = z.infer<
  typeof ReturnRequestDeactivateAutoTreatLogisticianFormSchema
>;

export function ActivityDeactivateAutoTreatLogistician({
  returnRequest,
}: ReturnRequestDeactivateAutoTreatLogisticianProps) {
  const t = useTranslation();
  const role = useViewerRole();
  const { data: shop } = useCurrentShopSuspense();
  const { mutateAsync: updateReturnRequestDeactivateAutoTreatLogistician } =
    useUpdateReturnRequestDeactivateAutoTreatLogistician();

  const form = useForm<ReturnRequestDeactivateAutoTreatLogisticianFormValues>({
    mode: "onChange",
    defaultValues: {
      deactivateAutoTreatLogistician:
        !!returnRequest.deactivateAutoTreatLogistician,
    },
  });

  const values = useWatch({ control: form.control });

  const onSubmit = form.handleSubmit(
    ({
      deactivateAutoTreatLogistician,
    }: ReturnRequestDeactivateAutoTreatLogisticianFormValues) => {
      updateReturnRequestDeactivateAutoTreatLogistician({
        params: {
          path: {
            shopId: returnRequest.shopId,
            returnRequestId: returnRequest.id,
          },
        },
        body: {
          deactivateAutoTreatLogistician,
        },
      }).then(() => form.reset({ deactivateAutoTreatLogistician }));
    },
  );

  useEffect(() => {
    if (form.formState.isDirty) onSubmit();
  }, [values, form.formState.isDirty]);

  if (
    role !== "logistician" &&
    shop?.workflowLogistician?.activated &&
    shop?.preferences?.autoTreateOnLogisticianValidate
  ) {
    return (
      <Form {...form}>
        <form
          className="flex-initial"
          title={t(
            "returnRequest.deactivateAutoTreatLogistician.fieldDescription",
          )}
        >
          <FormField
            control={form.control}
            name="deactivateAutoTreatLogistician"
            render={({ field }) => (
              <FormSwitch
                label={t("returnRequest.deactivateAutoTreatLogistician.title")}
                className="space-y-0 justify-end w-auto min-w-auto flex-initial"
                {...field}
              />
            )}
          />
        </form>
      </Form>
    );
  } else {
    return null;
  }
}
